/**
 * World hopping: travel from the world you're in to another one, and invite
 * the people around you to come along.
 *
 * - travelToWorld(): the one way to leave for another world (portals, the
 *   "Go to a world" panel, accepted invites). Fade, say goodbye on the room
 *   channel (gathrxr:leave-world), drop realtime channels, then
 *   location.replace(/vr/<id>?from=<here>) so the arriving page can put you
 *   beside its portal back (useWorldPortalArrival) and Back doesn't return to
 *   a half-alive copy of this world.
 * - Invites: invite_to_world() (server: permission check, rate limit, one
 *   pending row per invitee) and then a 'world:invite' broadcast on this
 *   world's room channel targeted at each invitee's presence key. The invitee
 *   verifies the row before showing the prompt; accept_world_invite() grants
 *   access to a private destination when the inviter may share it.
 *   See supabase/migrations/20261028_world_invites.sql.
 */
import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { getChannel, getPresenceKey } from './realtimeManager';
import { useWorldStore } from '../stores/worldStore';
import { canInteract } from '../stores/safetyStore';
import '../components/portals/portals.css'; // travel veil

// ---------------------------------------------------------------------------
// Safety hook point
// ---------------------------------------------------------------------------

/**
 * May we exchange invites with this user? The single hook for block / mute:
 * used when listing people to invite, when sending, and when an invite
 * arrives (a blocked sender's invites are dropped silently). Either side
 * blocking the other (safetyStore: user_blocks, or a session block of a
 * guest's presence key) means no invites. The server also refuses blocked
 * pairs in invite_to_world().
 */
export function canInvite(userId: string | null | undefined, presenceKey?: string | null): boolean {
  return canInteract(userId, presenceKey);
}

// ---------------------------------------------------------------------------
// Travel
// ---------------------------------------------------------------------------

/** The world this page shows (/world/:id or /vr/:id). */
export function currentWorldId(): string | null {
  const m = window.location.pathname.match(/^\/(?:world|vr)\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Full-screen fade with a line of text (styles: portals.css .gx-portal-veil). */
export function travelVeil(text: string): { set: (t: string) => void; close: () => void } {
  const el = document.createElement('div');
  el.className = 'gx-portal-veil';
  el.setAttribute('role', 'status');
  el.innerHTML = '<div class="gx-portal-veil-ring"></div><span></span>';
  const span = el.querySelector('span')!;
  span.textContent = text;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-on'));
  return {
    set: (t) => { span.textContent = t; },
    close: () => { el.classList.remove('is-on'); setTimeout(() => el.remove(), 400); },
  };
}

let tripBusy = false;
/** Load world `id` if this visitor may open it. Resolves false if they can't. */
export async function travelToWorld(id: string, name?: string): Promise<boolean> {
  if (tripBusy || !id) return false;
  tripBusy = true;
  const veil = travelVeil(`Travelling to ${name || 'another world'}…`);
  try {
    // Row-level security hides worlds this visitor can't open.
    const { data } = await supabase.from('worlds').select('id, name').eq('id', id).maybeSingle();
    if (!data) {
      veil.set('This world is private, or no longer exists.');
      setTimeout(() => { veil.close(); tripBusy = false; }, 2600);
      return false;
    }
    veil.set(`Travelling to ${data.name || 'another world'}…`);
    // Loading the page ends a headset session; the next world offers Enter VR again.
    const from = currentWorldId();
    const q = new URLSearchParams({ name: data.name || '' });
    if (from && from !== data.id) q.set('from', from);
    // Leave this world properly first (multiplayer, voice), then REPLACE the page.
    window.dispatchEvent(new Event('gathrxr:leave-world'));
    const go = () => window.location.replace(`/vr/${encodeURIComponent(data.id)}?${q}`);
    Promise.race([supabase.removeAllChannels(), new Promise((r) => setTimeout(r, 900))]).finally(() => setTimeout(go, 150));
    return true;
  } catch {
    veil.set('Could not open that world. Try again.');
    setTimeout(() => { veil.close(); tripBusy = false; }, 2600);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Worlds you can go to
// ---------------------------------------------------------------------------

export type HopSection = 'mine' | 'member' | 'featured' | 'public';
export const HOP_SECTIONS: { key: HopSection; label: string; short: string }[] = [
  { key: 'mine', label: 'My worlds', short: 'Mine' },
  { key: 'member', label: 'Shared with you', short: 'Shared' },
  { key: 'featured', label: 'Featured', short: 'Featured' },
  { key: 'public', label: 'Public worlds', short: 'Public' },
];

export interface HopWorld {
  id: string;
  name: string;
  thumb: string | null;
  isPublic: boolean;
  section: HopSection;
  /** May the viewer invite others here (public, or owner / builder / admin)? */
  canShare: boolean;
  ownerName?: string | null;
}

type Row = { id: string; name: string | null; thumbnail_url: string | null; settings: any; owner_id: string | null; owner?: { username?: string | null } | null };
const COLS = 'id, name, thumbnail_url, settings, owner_id, owner:profiles!worlds_owner_id_fkey(username)';
const isPublicRow = (w: Row) => !!(w.settings?.is_public || w.settings?.is_template) || !w.owner_id;

/**
 * Your worlds, worlds shared with you, featured and public ones (current world excluded).
 * Each world appears once, in its first section, unless `perSection` (the VR
 * Home's tabs: a featured public world is in both Featured and Public Worlds).
 */
export async function loadHopWorlds(exclude?: string | null, { perSection = false }: { perSection?: boolean } = {}): Promise<HopWorld[]> {
  const { data: { user } } = await supabase.auth.getUser();
  const none = Promise.resolve({ data: [] as any[] });
  const [mine, member, featured, pub, admin] = await Promise.all([
    user ? supabase.from('worlds').select(COLS).eq('owner_id', user.id).order('updated_at', { ascending: false }).limit(100) : none,
    user ? supabase.from('world_access').select(`role, world:worlds(${COLS})`).eq('user_id', user.id).limit(100) : none,
    supabase.from('featured_worlds').select(`rank, world:worlds(${COLS})`).order('rank', { ascending: true }).limit(30),
    supabase.from('worlds').select(COLS).eq('settings->is_public', true).order('updated_at', { ascending: false }).limit(80),
    user ? supabase.rpc('is_app_admin') : Promise.resolve({ data: false }),
  ]);
  const isAdmin = (admin as any)?.data === true;
  const seen = new Set<string>(exclude ? [exclude] : []);
  const out: HopWorld[] = [];
  const add = (w: Row | null | undefined, section: HopSection, canShare: boolean) => {
    const key = perSection ? `${section}:${w?.id}` : w?.id;
    if (!w?.id || !key || seen.has(key) || seen.has(w.id)) return;
    seen.add(key);
    out.push({
      id: w.id, name: w.name || 'Untitled world', thumb: w.thumbnail_url || null, isPublic: isPublicRow(w), section,
      canShare: canShare || isPublicRow(w) || isAdmin, ownerName: w.owner?.username ?? null,
    });
  };
  for (const w of (mine.data || []) as unknown as Row[]) add(w, 'mine', true);
  for (const r of (member.data || []) as unknown as { role: string; world: Row }[]) add(r.world, 'member', r.role === 'invited' || r.role === 'owner');
  for (const r of (featured.data || []) as unknown as { world: Row }[]) add(r.world, 'featured', false);
  for (const w of (pub.data || []) as unknown as Row[]) add(w, 'public', false);
  return out;
}

// ---------------------------------------------------------------------------
// Invites
// ---------------------------------------------------------------------------

export const INVITE_EVENT = 'world:invite';
export const INVITE_REPLY_EVENT = 'world:invite:reply';

export interface InviteTarget { key: string; userId?: string | null; userName: string }
export interface InviteResult { key: string; userName: string; ok: boolean; error?: string }

/** An invite shown to the person invited. */
export interface IncomingInvite {
  key: string;            // local id (dedupe)
  inviteId: string | null;
  worldId: string;
  worldName: string;
  fromName: string;
  fromKey: string | null;
  grantsAccess: boolean;
  at: number;
}

interface HopState {
  pickerOpen: boolean;
  invites: IncomingInvite[];
  /** Short status line for the inviter (e.g. "Sam is on the way"). */
  toast: string | null;
  setPickerOpen: (v: boolean) => void;
  pushInvite: (i: IncomingInvite) => void;
  dropInvite: (key: string) => void;
  showToast: (t: string) => void;
}
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export const useWorldHop = create<HopState>((set) => ({
  pickerOpen: false,
  invites: [],
  toast: null,
  setPickerOpen: (pickerOpen) => set({ pickerOpen }),
  pushInvite: (i) => set((s) => ({
    // One invite per (sender, world); newest first; at most 3 waiting.
    invites: [i, ...s.invites.filter((x) => !(x.worldId === i.worldId && (x.fromKey || x.fromName) === (i.fromKey || i.fromName)))].slice(0, 3),
  })),
  dropInvite: (key) => set((s) => ({ invites: s.invites.filter((x) => x.key !== key) })),
  showToast: (toast) => {
    clearTimeout(toastTimer);
    set({ toast });
    toastTimer = setTimeout(() => set({ toast: null }), 4200);
  },
}));
export const openWorldHop = () => useWorldHop.getState().setPickerOpen(true);

/** Map server errors (hint) to text worth showing. */
function rpcMessage(error: { message: string; hint?: string } | null, fallback: string): string {
  if (!error) return fallback;
  if (['auth_required', 'not_allowed', 'rate_limited', 'bad_input', 'missing', 'gone'].includes(error.hint || '')) return error.message;
  return fallback;
}

/**
 * Invite people in this room to `world`. Signed-in invitees go through the
 * server (permission + rate limit); guests can only be invited to worlds
 * anyone can open. Throws with a message fit to show when nobody could be invited.
 */
export async function sendWorldInvites(roomWorldId: string, world: HopWorld, targets: InviteTarget[]): Promise<InviteResult[]> {
  const state = useWorldStore.getState();
  const me = state.user?.id || null;
  if (!me) throw new Error('Sign in to invite people.');
  const allowed = targets.filter((t) => canInvite(t.userId, t.key));
  const users = [...new Set(allowed.map((t) => t.userId).filter((u): u is string => !!u && u !== me))];
  const results: InviteResult[] = [];
  const ids = new Map<string, { id: string; grants: boolean }>();
  if (users.length) {
    const { data, error } = await supabase.rpc('invite_to_world', { p_world: world.id, p_users: users, p_from_world: roomWorldId });
    if (error) throw new Error(rpcMessage(error, 'Couldn’t send invites. Try again.'));
    for (const r of (data || []) as { user_id: string; invite_id: string; grants_access: boolean }[]) ids.set(r.user_id, { id: r.invite_id, grants: r.grants_access });
  }
  const channel = getChannel(roomWorldId);
  const fromKey = getPresenceKey();
  for (const t of allowed) {
    const rec = t.userId ? ids.get(t.userId) : undefined;
    if (t.userId && !rec) { results.push({ key: t.key, userName: t.userName, ok: false, error: 'Couldn’t invite' }); continue; }
    if (!t.userId && !world.isPublic) { results.push({ key: t.key, userName: t.userName, ok: false, error: 'Guests can only join public worlds' }); continue; }
    try {
      await channel.send({
        type: 'broadcast', event: INVITE_EVENT,
        payload: {
          to: t.key, toUserId: t.userId || null, inviteId: rec?.id || null, grantsAccess: !!rec?.grants,
          worldId: world.id, worldName: world.name, fromName: state.userName || 'Someone', fromUserId: me, fromKey,
        },
      });
      results.push({ key: t.key, userName: t.userName, ok: true });
    } catch {
      results.push({ key: t.key, userName: t.userName, ok: false, error: 'Not delivered' });
    }
  }
  return results;
}

/** Validate an incoming 'world:invite' broadcast and queue the prompt. */
export async function receiveInvite(payload: any): Promise<void> {
  const myKey = getPresenceKey();
  const state = useWorldStore.getState();
  const me = state.user?.id || null;
  if (!payload || typeof payload.worldId !== 'string') return;
  if (payload.to !== myKey && !(payload.toUserId && payload.toUserId === me && !payload.to)) return;
  if (!canInvite(payload.fromUserId, typeof payload.fromKey === 'string' ? payload.fromKey : null)) return;
  let worldId: string = payload.worldId;
  let grantsAccess = false;
  if (payload.inviteId) {
    // Only trust what the server recorded (RLS: our own invites only).
    const { data } = await supabase.from('world_invites')
      .select('id, world_id, inviter_id, grants_access, status, expires_at')
      .eq('id', payload.inviteId).maybeSingle();
    if (!data || data.status !== 'pending' || new Date(data.expires_at).getTime() < Date.now()) return;
    if (payload.fromUserId && data.inviter_id !== payload.fromUserId) return;
    worldId = data.world_id;
    grantsAccess = !!data.grants_access;
  } else {
    // Unrecorded invite (we're a guest): only to worlds we can already open.
    const { data } = await supabase.rpc('world_access_state', { p_world: worldId });
    if (data !== 'ok') return;
  }
  if (worldId === currentWorldId()) return;
  useWorldHop.getState().pushInvite({
    key: payload.inviteId || `${payload.fromKey}:${worldId}:${Date.now()}`,
    inviteId: payload.inviteId || null,
    worldId,
    worldName: String(payload.worldName || 'a world').slice(0, 60),
    fromName: String(payload.fromName || 'Someone').slice(0, 40),
    fromKey: typeof payload.fromKey === 'string' ? payload.fromKey : null,
    grantsAccess,
    at: Date.now(),
  });
}

function reply(inv: IncomingInvite, accepted: boolean) {
  const room = currentWorldId();
  if (!room || !inv.fromKey) return;
  try {
    void getChannel(room).send({
      type: 'broadcast', event: INVITE_REPLY_EVENT,
      payload: { to: inv.fromKey, accepted, name: useWorldStore.getState().userName || 'Someone', worldName: inv.worldName },
    });
  } catch { /* best effort */ }
}

/** Go: record the acceptance (granting access if needed), then travel. Returns an error message, or null. */
export async function acceptInvite(inv: IncomingInvite): Promise<string | null> {
  let worldId = inv.worldId;
  if (inv.inviteId) {
    const { data, error } = await supabase.rpc('accept_world_invite', { p_invite: inv.inviteId });
    if (error) {
      useWorldHop.getState().dropInvite(inv.key);
      return rpcMessage(error, 'Couldn’t accept that invite.');
    }
    if (!data) { useWorldHop.getState().dropInvite(inv.key); return 'That invite has expired.'; }
    worldId = data as string;
  }
  reply(inv, true);
  useWorldHop.getState().dropInvite(inv.key);
  // Give the reply a moment to leave before the channels close.
  await new Promise((r) => setTimeout(r, 120));
  const ok = await travelToWorld(worldId, inv.worldName);
  return ok ? null : 'Couldn’t open that world.';
}

export function declineInvite(inv: IncomingInvite) {
  useWorldHop.getState().dropInvite(inv.key);
  if (inv.inviteId) void supabase.rpc('decline_world_invite', { p_invite: inv.inviteId });
  reply(inv, false);
}

/** Inviter side: someone answered. */
export function receiveReply(payload: any) {
  if (!payload || payload.to !== getPresenceKey()) return;
  const name = String(payload.name || 'Someone').slice(0, 40);
  useWorldHop.getState().showToast(payload.accepted ? `${name} is on the way to ${String(payload.worldName || 'the world').slice(0, 40)}` : `${name} said not now`);
}

/** Invites older than this disappear from the prompt (the server keeps them 30 min). */
export const INVITE_PROMPT_MS = 120_000;
